import { useId, useMemo } from "react";
import * as React from "react";
import {
  Box,
  FormControl,
  Select as MuiSelect,
  MenuItem,
  InputLabel,
  type SelectChangeEvent,
  IconButton,
  Checkbox as MuiCheckbox,
  ListItemText,
  Chip,
  FormHelperText,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { type ISelectObject } from "../../types/select";
import { SizingWrapper, type ISizingWrapperProps } from "../Wrapper";

export interface IProps extends ISizingWrapperProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: ISelectObject[];
  isClearable?: boolean;
  maxSelect?: number;
  error?: boolean;
  formHelperText?: string;
  /** MUI Selectの表示密度。省略時は従来どおり "medium"（既存の見た目を変えない） */
  muiSize?: "small" | "medium";
}

const MultiSelect: React.FC<IProps> = (props) => {
  const {
    label,
    options,
    value,
    onChange,
    isClearable = true,
    size,
    muiSize,
    maxSelect,
    error,
    formHelperText,
  } = props;
  // InputLabel と Select を紐づけるidは、同一画面に複数のMultiSelectがあっても
  // 重複しないようインスタンスごとに一意にする（固定idだとaria-labelが全て先頭のラベルに紐づく）
  // useId は ":r0:" のようにコロンを含み、CSSセレクタで使えないため英数字化する
  const reactId = useId().replace(/[^a-zA-Z0-9-]/g, "");
  const labelId = `multi-select-label-${reactId}`;
  const selectId = `multi-select-id-${reactId}`;
  const displayValueList = useMemo(
    () =>
      options
        .filter((item) => {
          return value.includes(item.value);
        })
        .map((item) => JSON.stringify(item)),
    [value, options]
  );

  const handleChange = (event: SelectChangeEvent<string[]>): void => {
    const selectedValues = event.target.value;
    const newValue =
      typeof selectedValues === "string"
        ? [selectedValues]
        : selectedValues.map(
            (jsonStr) => (JSON.parse(jsonStr) as ISelectObject).value
          );
    onChange(newValue);
  };

  const handleClearClick = (): void => {
    onChange([]);
  };

  const getDisabled = (option: ISelectObject): boolean => {
    if (maxSelect == null) {
      return false;
    }
    return (
      value.length >= maxSelect && !value.some((item) => item === option.value)
    );
  };

  return (
    <SizingWrapper size={size}>
      <FormControl fullWidth error={error} size={muiSize}>
        <InputLabel id={labelId} error={error}>
          {label}
        </InputLabel>
        <MuiSelect
          id={selectId}
          labelId={labelId}
          label={label}
          size={muiSize}
          onChange={handleChange}
          multiple
          value={displayValueList}
          defaultValue={[]}
          error={error}
          sx={{
            [`#${selectId}`]: {
              pr: 0,
            },
          }}
          renderValue={(selected) => (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              {selected.map((jsonStr) => {
                const parsed = JSON.parse(jsonStr) as ISelectObject;
                return (
                  <Chip
                    key={parsed.value}
                    label={parsed.label}
                    sx={{ cursor: "pointer" }}
                  />
                );
              })}
            </Box>
          )}
          endAdornment={
            isClearable ? (
              <IconButton
                sx={{
                  display:
                    displayValueList.length === 0 ? "none" : "inline-flex",
                  marginRight: "10px",
                }}
                onClick={handleClearClick}
              >
                <ClearIcon />
              </IconButton>
            ) : (
              <></>
            )
          }
        >
          {options.map((option, index) => (
            <MenuItem
              key={index}
              value={JSON.stringify(option)}
              disabled={getDisabled(option)}
            >
              <MuiCheckbox
                checked={value.some((item) => item === option.value)}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </MuiSelect>
        {formHelperText ? (
          <FormHelperText error={error}>{formHelperText}</FormHelperText>
        ) : null}
      </FormControl>
    </SizingWrapper>
  );
};

export { MultiSelect, type IProps as MultiSelectIProps };
